import type { EventRegistration, RegistrationStatus } from '../../types/event';
import type { Vehicle } from '../../types/vehicle';
import type { LimitedDesignationVehicleSearchResult } from '../../services/api/limitedDesignations';
import { organizerStatusLabel } from '../../utils/registrationStatus';

export type BuildPickerOption = {
  id: number;
  label: string;
  subtitle?: string;
  keywords?: string;
  imageUrl?: string | null;
  ownerUsername?: string;
  publicSlug?: string | null;
};

/** Match badge_bulk_issue.php: any registration except withdrawn can receive a badge. */
const EXCLUDED_ISSUE_STATUSES = new Set<string>(['withdrawn']);

export function formatVehicleYmm(parts: {
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
}): string {
  return [parts.year, parts.make, parts.model, parts.trim].filter(Boolean).join(' ').trim();
}

export function formatBuildLabel(parts: {
  nickname?: string | null;
  display_name?: string | null;
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  id?: number;
}): string {
  const nickname = (parts.nickname || parts.display_name || '').trim();
  const ymm = formatVehicleYmm(parts);
  if (nickname && ymm && nickname.toLowerCase() !== ymm.toLowerCase()) {
    return `${nickname} · ${ymm}`;
  }
  return nickname || ymm || (parts.id ? `Vehicle #${parts.id}` : 'Untitled build');
}

export function formatOwnerHandle(username?: string | null, displayName?: string | null): string {
  const handle = (username || '').trim();
  const name = (displayName || '').trim();
  if (handle && name && name.toLowerCase() !== handle.toLowerCase()) {
    return `${name} · @${handle}`;
  }
  if (handle) return `@${handle}`;
  return name || 'Unknown owner';
}

export function matchesBuildPickerQuery(option: BuildPickerOption, query: string): boolean {
  const q = query.trim().toLowerCase().replace(/^@+/, '');
  if (!q) return true;
  const hay = [String(option.id), option.label, option.subtitle, option.keywords]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((token) => hay.includes(token.replace(/^@+/, '')));
}

export function optionFromGarageVehicle(v: Vehicle): BuildPickerOption {
  const slug = (v as Vehicle & { public_slug?: string }).public_slug || v.slug;
  const label = formatBuildLabel(v);
  const bits: string[] = [];
  if (slug) bits.push(`/${slug}`);
  if (v.is_public === false) bits.push('Private');
  return {
    id: v.id,
    label,
    subtitle: bits.length ? bits.join(' · ') : undefined,
    keywords: [String(v.id), v.nickname, v.display_name, v.year, v.make, v.model, v.trim, slug]
      .filter(Boolean)
      .join(' '),
  };
}

export function isIssueableRegistration(status: RegistrationStatus | string | null | undefined): boolean {
  return !EXCLUDED_ISSUE_STATUSES.has(String(status || '').trim());
}

type LooseRegistration = EventRegistration & {
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  nickname?: string | null;
  public_slug?: string | null;
};

export function optionFromEventRegistration(reg: EventRegistration): BuildPickerOption | null {
  const row = reg as LooseRegistration;
  const vehicleId = Number(row.vehicle_id || row.vehicle?.id || 0);
  if (!vehicleId || !isIssueableRegistration(row.status)) return null;
  const v = row.vehicle;
  const username = row.owner?.username || row.username || '';
  const firstName = row.owner?.first_name || row.first_name || '';
  const lastName = row.owner?.last_name || row.last_name || '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const label = formatBuildLabel({
    id: vehicleId,
    nickname: v?.nickname || row.nickname,
    year: v?.year ?? row.year,
    make: v?.make || row.make,
    model: v?.model || row.model,
    trim: v?.trim || row.trim,
  });
  const ownerLine = formatOwnerHandle(username, displayName);
  const status = organizerStatusLabel(row.status);
  return {
    id: vehicleId,
    label,
    subtitle: `${ownerLine} · ${status}`,
    keywords: [
      String(vehicleId),
      String(row.user_id || ''),
      v?.year ?? row.year,
      v?.make || row.make,
      v?.model || row.model,
      v?.trim || row.trim,
      v?.nickname || row.nickname,
      v?.public_slug || row.public_slug,
      username,
      firstName,
      lastName,
      displayName,
      status,
    ]
      .filter(Boolean)
      .join(' '),
  };
}

export function optionFromAdminVehicleSearch(v: LimitedDesignationVehicleSearchResult): BuildPickerOption {
  const ownerName = (v.owner_name || '').trim();
  return {
    id: v.id,
    label: formatBuildLabel(v),
    subtitle: formatOwnerHandle(v.username, ownerName),
    keywords: [
      String(v.id),
      v.year,
      v.make,
      v.model,
      v.nickname,
      v.username,
      ownerName,
      v.public_slug,
      v.public_url,
    ]
      .filter(Boolean)
      .join(' '),
    imageUrl: v.hero_image_url || null,
    ownerUsername: v.username || undefined,
    publicSlug: v.public_slug,
  };
}
